export const WRITING_CATEGORIES = [
  'Blog Writing',
  'Article Writing',
  'Copywriting',
  'Technical Writing',
  'Academic Writing',
  'Creative Writing / Fiction',
  'Ghostwriting',
  'SEO Content Writing',
  'Social Media Content',
  'Email & Newsletter Writing',
  'Product Descriptions',
  'Press Releases',
  'Grant Writing',
  'Scriptwriting',
  'White Papers & Case Studies',
  'Editing & Proofreading',
  'Resume & Cover Letter Writing',
  'UX Writing',
  'Translation & Localization',
  'Other',
] as const;

export const TASK_STATUSES = [
  'draft',
  'assigned',
  'in_progress',
  'submitted',
  'revision_requested',
  'approved',
  'cancelled',
] as const;

export const EMPLOYER_TYPES = [
  'individual',
  'startup',
  'ecommerce',
  'affiliate',
  'agency',
  'corporate',
] as const;

// Legacy writer-profile enums (kept for backward compatibility with existing
// writer profile rows that reference these values). NEW task-posting forms
// should use TASK_CONTENT_TYPES / TASK_PRODUCT_TYPES below.
export const CONTENT_TYPES = [
  'Product Reviews',
  'Product Comparisons',
  'Best Of Articles',
  'Buyer Guides',
  'How-To Articles',
  'Listicles',
  'Feature Roundups',
] as const;

export const PRODUCT_CATEGORIES = [
  'Smartphones & Accessories',
  'Laptops & Computers',
  'Tablets & eReaders',
  'Wearables',
  'Headphones & Earbuds',
  'Cameras & Photography',
  'Smart Home Devices',
  'Home Entertainment',
  'Gaming & Consoles',
  'PC Gaming & Accessories',
  'Power & Charging',
  'Networking & Connectivity',
  'Storage Devices',
  'AI Gadgets & Assistants',
  'Electric Mobility & Tech',
  'Others',
] as const;

// -- Unified category taxonomy ------------------------------------------------
//
// ONE source of truth for the employer job-posting category AND the writer's
// claimed industries. Previously these were two divergent lists
// (`TASK_CATEGORIES` and `WRITER_INDUSTRIES`); they are now the same union so
// the matcher's category hard-filter compares apples to apples. Every existing
// string from BOTH original lists is preserved verbatim.
export const CATEGORIES = [
  'Technology',
  'Beauty',
  'Fashion',
  'Travel',
  'Sports',
  'Food',
  'Automotive',
  'Baby Products',
  'Finance',
  'Fitness and Wellbeing',
  'Others',
] as const;
export type Category = (typeof CATEGORIES)[number];

// Backward-compatible aliases — all existing imports keep working unchanged.
export const TASK_CATEGORIES = CATEGORIES;
export type TaskCategory = Category;

// Availability flag — only enabled categories can be picked; the rest render
// as disabled "coming soon" options in the UI. Now keyed over the full unified
// taxonomy; only Technology is live.
export const TASK_CATEGORY_AVAILABILITY: Record<TaskCategory, boolean> = {
  Technology: true,
  Beauty: false,
  Fashion: false,
  Travel: false,
  Sports: false,
  Food: false,
  Automotive: false,
  'Baby Products': false,
  Finance: false,
  'Fitness and Wellbeing': false,
  Others: false,
};

export const TASK_CONTENT_TYPES = [
  'Deals',
  'Explainer Articles',
  "How To's",
  'News Article',
  'Newsletter',
  'Opinion Article',
  'Product Comparison',
] as const;
export type TaskContentType = (typeof TASK_CONTENT_TYPES)[number];

// Product types scoped per category. For now only Technology has entries;
// other categories will be filled in as they launch.
export const TASK_PRODUCT_TYPES_BY_CATEGORY = {
  Technology: [
    'Smartphones & Accessories',
    'Laptops & Computing',
    'Wearables',
    'Headphones & Earbuds',
    'Cameras & Photography',
    'Smart Home Devices',
    'TV and Audio',
    'Gaming & Consoles',
    'Networking & Connectivity',
    'Other',
  ],
  Beauty: [],
  Fashion: [],
  Travel: [],
  Sports: [],
  Food: [],
  Automotive: [],
  'Baby Products': [],
  Finance: [],
  'Fitness and Wellbeing': [],
  Others: [],
} as const satisfies Record<TaskCategory, readonly string[]>;

// Flat list of all valid product type values across every category — used for
// Zod enum validation. Keep "Other" included.
const _allProductTypes: readonly string[] = Array.from(
  new Set(
    Object.values(TASK_PRODUCT_TYPES_BY_CATEGORY).flatMap(
      (list) => list as readonly string[],
    ),
  ),
);
export const TASK_PRODUCT_TYPES = _allProductTypes as unknown as readonly [
  string,
  ...string[],
];

export const WRITER_EXPERIENCE_LEVELS = [
  'beginner',
  'associate',
  'intermediate',
  'senior',
  'expert',
] as const;
export type WriterExperienceLevel = (typeof WRITER_EXPERIENCE_LEVELS)[number];

export const WRITER_EXPERIENCE_LABELS: Record<WriterExperienceLevel, string> = {
  beginner: 'Beginner (<1 year)',
  associate: 'Associate (1–3 yrs)',
  intermediate: 'Intermediate (3–5 yrs)',
  senior: 'Senior (5–10 yrs)',
  expert: 'Expert (10+ yrs)',
};

// Legacy experience enum — retained for rows saved before the v2 redesign.
export const EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'advance', 'expert'] as const;

export const WORD_COUNT_RANGES = ['500-800', '800-1200', '1200-2000', '2000+'] as const;

/**
 * Parse a `word_count_range` label into the numeric bounds stored on
 * `tasks.min_word_count` / `tasks.max_word_count` (TASK-12).
 *
 * Both create paths used to hard-code those columns to null while storing the
 * range only as a display string, so the editor's submit gate, the submit
 * modal's min/max rows and the `WordCount` range display all read null — a
 * writer could submit 50 words against an "800–1,200 words" task with no
 * warning, and the enforcement UI could never fire for any task created
 * through the app.
 *
 * "2000+" is open-ended: a minimum with no maximum.
 */
export function parseWordCountRange(
  range: string | null | undefined,
): { min: number | null; max: number | null } {
  if (!range) return { min: null, max: null };

  const openEnded = /^(\d+)\+$/.exec(range);
  if (openEnded) return { min: Number(openEnded[1]), max: null };

  const bounded = /^(\d+)\s*-\s*(\d+)$/.exec(range);
  if (bounded) return { min: Number(bounded[1]), max: Number(bounded[2]) };

  return { min: null, max: null };
}

export const RESEARCH_LEVELS = ['beginner', 'intermediate', 'advance', 'expert'] as const;

export const WRITING_TONES = ['freestyle', 'brand_lead'] as const;

export const BRAND_VOICES = ['brand_guideline', 'freestyle'] as const;
export type BrandVoice = (typeof BRAND_VOICES)[number];

export const BRAND_VOICE_LABELS: Record<BrandVoice, string> = {
  brand_guideline: 'Follow brand guideline',
  freestyle: 'Writer can freestyle',
};

export const WRITER_EXPERIENCE_YEARS = [
  'less_than_1',
  '1_3',
  '3_5',
  '5_10',
  '10_plus',
] as const;
export type WriterExperienceYears = (typeof WRITER_EXPERIENCE_YEARS)[number];

/**
 * Bug #1 fix (QA 2026-05-23, migration 00060) — writer onboarding
 * captures the user's experience in *year-range* labels but the
 * matcher (and the rest of the task system) is unified on the 5-tier
 * `WRITER_EXPERIENCE_LEVELS` taxonomy. This map converts the form
 * value into the canonical tier at write time so the writer's
 * `writer_profiles.experience_level` stays in lock-step with what the
 * eligibility filter recognizes.
 *
 * Source of truth: src/lib/actions/onboarding.ts uses this when
 * inserting/updating writer_profiles rows. Do NOT bypass it — storing
 * a raw year-range value will re-trigger the empty-slate bug.
 */
export const WRITER_EXPERIENCE_YEARS_TO_LEVEL: Record<
  WriterExperienceYears,
  WriterExperienceLevel
> = {
  less_than_1: 'beginner',
  '1_3': 'associate',
  '3_5': 'intermediate',
  '5_10': 'senior',
  '10_plus': 'expert',
};

/**
 * Inverse of `WRITER_EXPERIENCE_YEARS_TO_LEVEL`. Use this when reading
 * a writer's canonical-tier `writer_profiles.experience_level` back into
 * a form whose value-set is the year-range scheme (the existing settings
 * tabs + profile-edit-form). After the form is migrated to use 5-tier
 * values directly this map can be retired.
 */
export const WRITER_EXPERIENCE_LEVEL_TO_YEARS: Record<
  WriterExperienceLevel,
  WriterExperienceYears
> = {
  beginner: 'less_than_1',
  associate: '1_3',
  intermediate: '3_5',
  senior: '5_10',
  expert: '10_plus',
};

// Writer-claimed industries. Aliased onto the unified `CATEGORIES` taxonomy so
// the matcher can compare a task's category against a writer's industries
// directly (see src/lib/matching/scoring.ts). Kept as a named export because
// ~6 call sites (validations, onboarding/profile forms) import it.
export const WRITER_INDUSTRIES = CATEGORIES;

// Languages a task's submission may be required in. Stored as the language
// name (not an ISO code) to stay consistent with writer_profiles.languages.
export const LANGUAGES = [
  'English - American',
  'English - UK',
  'Filipino',
  'Spanish',
  'French',
  'German',
  'Portuguese',
] as const;
export type Language = (typeof LANGUAGES)[number];

export const WRITER_CONTENT_TYPES = [
  'Deals',
  'Explainer Articles',
  "How To's",
  'News Article',
  'Newsletter',
  'Opinion Article',
] as const;

export const WRITER_LANGUAGES = ['English - American', 'English - UK'] as const;

// All ISO 639-1 languages by English name. Used by the writer-onboarding
// "Writing Language" multi-select. Stored in writer_profiles.languages (TEXT[]).
export const ALL_LANGUAGES = [
  'Abkhazian',
  'Afar',
  'Afrikaans',
  'Akan',
  'Albanian',
  'Amharic',
  'Arabic',
  'Aragonese',
  'Armenian',
  'Assamese',
  'Avaric',
  'Avestan',
  'Aymara',
  'Azerbaijani',
  'Bambara',
  'Bashkir',
  'Basque',
  'Belarusian',
  'Bengali',
  'Bihari',
  'Bislama',
  'Bosnian',
  'Breton',
  'Bulgarian',
  'Burmese',
  'Catalan',
  'Chamorro',
  'Chechen',
  'Chichewa',
  'Chinese - Mandarin',
  'Chinese - Cantonese',
  'Chuvash',
  'Cornish',
  'Corsican',
  'Cree',
  'Croatian',
  'Czech',
  'Danish',
  'Divehi',
  'Dutch',
  'Dzongkha',
  'English - American',
  'English - UK',
  'English - Australian',
  'English - Canadian',
  'English - Indian',
  'Esperanto',
  'Estonian',
  'Ewe',
  'Faroese',
  'Fijian',
  'Filipino',
  'Finnish',
  'French',
  'Fulah',
  'Galician',
  'Ganda',
  'Georgian',
  'German',
  'Greek',
  'Guarani',
  'Gujarati',
  'Haitian Creole',
  'Hausa',
  'Hebrew',
  'Herero',
  'Hindi',
  'Hiri Motu',
  'Hungarian',
  'Icelandic',
  'Ido',
  'Igbo',
  'Indonesian',
  'Interlingua',
  'Interlingue',
  'Inuktitut',
  'Inupiaq',
  'Irish',
  'Italian',
  'Japanese',
  'Javanese',
  'Kalaallisut',
  'Kannada',
  'Kanuri',
  'Kashmiri',
  'Kazakh',
  'Khmer',
  'Kikuyu',
  'Kinyarwanda',
  'Kirghiz',
  'Komi',
  'Kongo',
  'Korean',
  'Kurdish',
  'Kuanyama',
  'Lao',
  'Latin',
  'Latvian',
  'Limburgish',
  'Lingala',
  'Lithuanian',
  'Luba-Katanga',
  'Luxembourgish',
  'Macedonian',
  'Malagasy',
  'Malay',
  'Malayalam',
  'Maltese',
  'Manx',
  'Maori',
  'Marathi',
  'Marshallese',
  'Mongolian',
  'Nauru',
  'Navajo',
  'Ndonga',
  'Nepali',
  'North Ndebele',
  'Northern Sami',
  'Norwegian',
  'Norwegian Bokmål',
  'Norwegian Nynorsk',
  'Occitan',
  'Ojibwa',
  'Old Church Slavonic',
  'Oriya',
  'Oromo',
  'Ossetian',
  'Pali',
  'Pashto',
  'Persian',
  'Polish',
  'Portuguese',
  'Portuguese - Brazilian',
  'Punjabi',
  'Quechua',
  'Romanian',
  'Romansh',
  'Rundi',
  'Russian',
  'Samoan',
  'Sango',
  'Sanskrit',
  'Sardinian',
  'Scottish Gaelic',
  'Serbian',
  'Shona',
  'Sindhi',
  'Sinhala',
  'Slovak',
  'Slovenian',
  'Somali',
  'South Ndebele',
  'Southern Sotho',
  'Spanish',
  'Spanish - Latin American',
  'Sundanese',
  'Swahili',
  'Swati',
  'Swedish',
  'Tagalog',
  'Tahitian',
  'Tajik',
  'Tamil',
  'Tatar',
  'Telugu',
  'Thai',
  'Tibetan',
  'Tigrinya',
  'Tonga',
  'Tsonga',
  'Tswana',
  'Turkish',
  'Turkmen',
  'Twi',
  'Uighur',
  'Ukrainian',
  'Urdu',
  'Uzbek',
  'Venda',
  'Vietnamese',
  'Volapük',
  'Walloon',
  'Welsh',
  'Western Frisian',
  'Wolof',
  'Xhosa',
  'Yiddish',
  'Yoruba',
  'Zhuang',
  'Zulu',
] as const;
export type AllLanguage = (typeof ALL_LANGUAGES)[number];

// Username field constraints (PRD: writer + employer onboarding)
export const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

export const PLATFORM_FEE_WRITER_WITHDRAWAL = 0.05; // 5% per PRD §5.5.3
export const MINIMUM_WITHDRAWAL_AMOUNT = 1; // $1.00 minimum
// Workspace subscription price, stored in cents. The Stripe recurring Price
// (scripts/stripe/create-price.ts) is created at WORKSPACE_SUBSCRIPTION_PRICE / 100
// dollars; the live Price id is referenced at runtime via STRIPE_PRICE_ID.
export const WORKSPACE_SUBSCRIPTION_PRICE = 5000; // cents ($50/mo)
export const WORKSPACE_SUBSCRIPTION_CURRENCY = 'usd';
export const WORKSPACE_TRIAL_DAYS = 14;
export const OTP_EXPIRY_MINUTES = 10;
export const OTP_MAX_ATTEMPTS = 5;
export const OTP_LOCKOUT_MINUTES = 15;
export const RATING_PROMPT_EXPIRY_DAYS = 7;
export const MAX_COMMENT_LENGTH = 2000;
export const MAX_REVIEW_LENGTH = 500;
